import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { AGENTS, DEPARTMENTS, APPROVAL_CHAINS } from '../../utils/agents';
import { useApprovalStore } from '../../stores/approvalStore';

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'] as const;

interface CreateApprovalModalProps {
  onClose: () => void;
}

export default function CreateApprovalModal({ onClose }: CreateApprovalModalProps) {
  const createApproval = useApprovalStore((s) => s.createApproval);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<string>('medium');
  const [submittedBy, setSubmittedBy] = useState('');
  const [approver, setApprover] = useState('');
  const [department, setDepartment] = useState('');
  const [linkedTaskId, setLinkedTaskId] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  function handleAgentChange(slug: string) {
    setSubmittedBy(slug);
    if (slug) {
      const agent = AGENTS.find((a) => a.slug === slug);
      if (agent) {
        setDepartment(agent.department);
      }
      // Auto-fill approver from APPROVAL_CHAINS
      const approverSlug = APPROVAL_CHAINS[slug];
      if (approverSlug && approverSlug !== 'owner') {
        setApprover(approverSlug);
      } else if (approverSlug === 'owner') {
        setApprover('owner');
      } else {
        setApprover('');
      }
    } else {
      setApprover('');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!submittedBy) {
      setError('Submitted by is required');
      return;
    }
    if (!approver) {
      setError('Approver is required');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await createApproval({
        title: title.trim(),
        description: description.trim() || null,
        priority: priority as 'low' | 'medium' | 'high' | 'urgent',
        submitted_by: submittedBy,
        approver,
        department: department || null,
        linked_task_id: linkedTaskId ? parseInt(linkedTaskId, 10) : null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create approval');
      setSubmitting(false);
    }
  }

  // C-Suite agents for approver dropdown
  const cSuiteAgents = AGENTS.filter((a) => a.isCSuite);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-bg-secondary border border-border-subtle rounded-xl p-6 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-text-primary">New Approval</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              placeholder="Approval title"
              autoFocus
            />
            {error ? <p className="text-error text-xs mt-1">{error}</p> : null}
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple resize-none"
              placeholder="Optional description"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Submitted By *</label>
            <select
              value={submittedBy}
              onChange={(e) => handleAgentChange(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
            >
              <option value="">Select agent...</option>
              {AGENTS.map((a) => (
                <option key={a.slug} value={a.slug}>
                  {a.name} -- {a.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Approver *</label>
            <select
              value={approver}
              onChange={(e) => setApprover(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
            >
              <option value="">Select approver...</option>
              <option value="owner">Owners (Aaron & Taylor)</option>
              {cSuiteAgents.map((a) => (
                <option key={a.slug} value={a.slug}>
                  {a.name} -- {a.shortName}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Linked Task ID</label>
            <input
              type="number"
              value={linkedTaskId}
              onChange={(e) => setLinkedTaskId(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              placeholder="Optional task ID"
            />
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <button
              type="button"
              onClick={handleClose}
              className="text-text-muted hover:text-text-secondary text-sm transition-colors px-3 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
